import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ChaafoLogo from "../Images/Restaurant_Logo_Placeholder.webp";
import NoImg from "../Images/Item_Image_Placeholder.webp";

import { useCart } from "../Context/CartContext";
import { useGlobalContext } from "../Context/GlobalContext";
import LoadingScreen from "../Utils/LoadingScreen";
   
import useOfferItems from "../Services/useOfferItems";
import { useRestaurantDetails } from "../Services/useRestaurantDetails";

import RepeatCustomizationModal from "./RepeatCustomizationModal";
import {
  buildCustomizationHistoryKey,
  syncCustomizationHistoryWithCart,
} from "../Utils/customizationPreferenceHistory";
import CartDrawer from "./CartDrawer";
import { ArrowLeft, ArrowRight, Loader2, Star, X } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";

const ShimmerStyles = () => (
  <style>
    {`
      @keyframes chaafoShimmer {
        0% {
          background-position: 200% 0;
        }

        100% {
          background-position: -200% 0;
        }
      }

      .chaafo-shimmer {
        background: linear-gradient(
          90deg,
          #e5e7eb 0%,
          #e5e7eb 30%,
          #f8fafc 50%,
          #e5e7eb 70%,
          #e5e7eb 100%
        );

        background-size: 220% 100%;

        animation: chaafoShimmer 1.35s ease-in-out infinite;
      }
    `}
  </style>
);

const ItemImageWithShimmer = ({
  src,
  alt,
  fallback = NoImg,
  className = "",
}) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
  }, [src]);

  return (
    <div className="relative h-full w-full overflow-hidden bg-white">
      {/* SHIMMER */}

      {isLoading && (
        <div className="chaafo-shimmer absolute inset-0 z-[1] h-full w-full" />
      )}

      {/* IMAGE */}

      <img
        src={src || fallback}
        alt={alt || "Item"}
        loading="lazy"
        onLoad={() => {
          setIsLoading(false);
        }}
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = fallback;

          setIsLoading(false);
        }}
        className={`
          block
          h-full
          w-full
          object-cover
          bg-white

          transition-opacity
          duration-300

          ${isLoading ? "opacity-0" : "opacity-100"}

          ${className}
        `}
      />
    </div>
  );
};

// =========================================================
// PRICE OPTIONS
// =========================================================

const getOfferPriceOptions = (item) => {
  const prices = Array.isArray(item?.prices)
    ? item.prices
    : [];

  if (prices.length === 0) {
    return [];
  }

  return prices.map((priceItem, index) => {
    const originalPrice = Number(
      priceItem?.price || 0,
    );

    const discountPrice = Number(
      priceItem?.discount_price || 0,
    );

    const hasDiscount =
      discountPrice > 0 &&
      discountPrice < originalPrice;

    const sellingPrice = hasDiscount
      ? discountPrice
      : originalPrice;

    return {
      index,

      size:
        String(
          priceItem?.size ||
            priceItem?.size_trans ||
            "Regular",
        ).trim() || "Regular",

      sizeId:
        String(
          priceItem?.size_id ||
            index,
        ).trim(),

      originalPrice,

      sellingPrice,

      hasDiscount,

      formattedSellingPrice: hasDiscount
        ? priceItem?.formatted_discount_price ||
          `₹${sellingPrice}`
        : priceItem?.formatted_price ||
          `₹${sellingPrice}`,

      formattedOriginalPrice: hasDiscount
        ? priceItem?.formatted_price ||
          `₹${originalPrice}`
        : "",

      customizations: Array.isArray(
        priceItem?.customizations,
      )
        ? priceItem.customizations
        : [],

      raw: priceItem,
    };
  });
};


// =========================================================
// BUILD SELECTED VARIANT
// =========================================================

const buildOfferVariantItem = (
  item,
  priceOption,
) => {
  if (!priceOption) {
    return item;
  }

  return {
    ...item,

    prices: priceOption?.raw
      ? [priceOption.raw]
      : item?.prices,

    size: priceOption.size,

    sizeId: priceOption.sizeId,

    size_id: priceOption.sizeId,

    selected_size:
      priceOption.size,

    selected_size_id:
      priceOption.sizeId,

    price:
      priceOption.originalPrice,

    unitPrice:
      priceOption.sellingPrice,

    itemPrice:
      priceOption.sellingPrice,

    item_price:
      priceOption.sellingPrice,

    formattedPrice:
      priceOption.hasDiscount
        ? priceOption.formattedOriginalPrice
        : priceOption.formattedSellingPrice,

    finalFormattedPrice:
      priceOption.formattedSellingPrice,

    hasDiscount:
      priceOption.hasDiscount,

    selectedPrice:
      priceOption.raw,
  };
};

const OfferItemsSkeleton = () => {
  return (
    <div className="space-y-6">
      {/* ================================================ */}
      {/* RESTAURANT HEADER */}
      {/* ================================================ */}

      <div className="flex items-center gap-3">
        <div
          className="
            chaafo-shimmer
            h-11
            w-11
            shrink-0
            rounded-xl

            sm:h-12
            sm:w-12
          "
        />

        <div className="flex-1 space-y-2">
          <div
            className="
              chaafo-shimmer
              h-4
              w-[145px]
              rounded-full
            "
          />

          <div
            className="
              chaafo-shimmer
              h-3
              w-[90px]
              rounded-full
            "
          />
        </div>
      </div>

      <div className="h-px bg-gray-200" />

      {/* ================================================ */}
      {/* ITEM CARDS */}
      {/* ================================================ */}

      <div
        className="
          grid
          grid-cols-2
          gap-4

          md:grid-cols-3
          lg:grid-cols-4
        "
      >
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className="
              overflow-hidden

              rounded-xl
              border
              border-gray-200

              bg-white

              shadow-sm

              sm:rounded-2xl
            "
          >
            {/* ========================================== */}
            {/* IMAGE */}
            {/* ========================================== */}

            <div
              className="
                chaafo-shimmer
                h-[100px]
                w-full
                min-[480px]:h-[120px]
                sm:h-[130px]
                md:h-[140px]
                lg:h-[150px]
              "
              style={{
                animationDelay: `${index * 80}ms`,
              }}
            />

            {/* ========================================== */}
            {/* CONTENT */}
            {/* ========================================== */}

            <div className="p-2 sm:p-4">
              {/* ITEM NAME */}

              <div
                className="
                  chaafo-shimmer
                  h-[15px]
                  w-[78%]
                  rounded-full

                  sm:h-4
                "
              />

              {/* TAGS */}

              <div className="mt-3 flex items-center gap-2">
                <div
                  className="
                    chaafo-shimmer
                    h-5
                    w-[48px]
                    rounded-full
                  "
                />

                <div
                  className="
                    chaafo-shimmer
                    h-5
                    w-[82px]
                    rounded-full
                  "
                />
              </div>

              {/* PRICE + BUTTON */}

              <div
                className="
                  mt-4
                  flex
                  items-center
                  justify-between
                  gap-3
                "
              >
                <div
                  className="
                    chaafo-shimmer
                    h-5
                    w-[55px]
                    rounded-md
                  "
                />

                <div
                  className="
                    chaafo-shimmer
                    h-8
                    w-[64px]
                    rounded-full

                    sm:h-9
                    sm:w-[72px]
                  "
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const OfferItems = () => {
  const { value } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const title = location.state?.title || `Items Under ₹${value}`;

  const selectedBanner = location.state?.selectedBanner || null;

  const getBannerUrl = (mediaUrl, type) => {
    if (!mediaUrl) {
      return "";
    }

    const regex = new RegExp(`${type}:\\s*([^|]+)`, "i");

    return mediaUrl.match(regex)?.[1]?.trim() || "";
  };

  const bannerMobileUrl = getBannerUrl(selectedBanner?.media_url, "mobile");

  const bannerTabletUrl = getBannerUrl(selectedBanner?.media_url, "tablet");

  const bannerDesktopUrl = getBannerUrl(selectedBanner?.media_url, "desktop");

  const bannerFallbackUrl =
    bannerMobileUrl || bannerTabletUrl || bannerDesktopUrl;

  // =========================================================
  // GLOBAL CONTEXT
  // =========================================================

  const {
    selectedOrderType,
    selectedPlatform,
    selectRestaurant,
    selectedDeliveryLocation,
  } = useGlobalContext();
  // =========================================================
  // OFFER ITEMS
  // =========================================================

  const { items, loading, loadingMore, error, hasMore, fetchOfferData } =
    useOfferItems();

  const { getRestaurantDetails } = useRestaurantDetails();

  const [checkingRestaurantId, setCheckingRestaurantId] = useState("");

  // =========================================================
  // STABLE RESTAURANT ORDER FOR INFINITE SCROLL
  // =========================================================

  const lastLoadMoreTimeRef = useRef(0);

  const LOAD_MORE_COOLDOWN = 1200;

  const isOfferRestaurantClosed = (restaurant) => {
    const catalogueStatus = String(
      restaurant?.catalogueStatus || restaurant?.catalogue_status || "",
    )
      .trim()
      .toLowerCase();

    // show = OPEN
    // hide / empty / anything else = CLOSED
    return catalogueStatus !== "show";
  };

  // =========================================================
  // CART
  // =========================================================

  const {
    items: cartItems,
    itemCount,
    addToCart,
    decreaseItem,
    getQuantity,
  } = useCart();

  useEffect(() => {
    const currentCartHistory = syncCustomizationHistoryWithCart(cartItems);

    setCustomizationHistory(currentCartHistory);

    if (cartItems.length === 0) {
      setRepeatCustomizationItem(null);
    }
  }, [cartItems]);

  const [cartOpen, setCartOpen] = useState(false);

  const [selectedItemDetail, setSelectedItemDetail] = useState(null);

  const [selectedCustomizationItem, setSelectedCustomizationItem] =
    useState(null);

  const [customizationValues, setCustomizationValues] = useState({});

  const [selectedPriceIndexes, setSelectedPriceIndexes] = useState({});

  const [selectedDetailPriceIndex, setSelectedDetailPriceIndex] = useState(0);

  // =========================================================
  // RESTAURANT SLIDER POSITION
  // =========================================================

  const getResponsiveItemsPerPage = () => {
    if (typeof window === "undefined") {
      return 4;
    }

    const width = window.innerWidth;

    // Desktop
    if (width >= 1024) {
      return 4;
    }

    // Tablet
    if (width >= 768) {
      return 3;
    }

    // Mobile
    return 2;
  };

  const restaurantSliderRefs = useRef({});

  const [sliderEdgeState, setSliderEdgeState] = useState({});
  const [noDataFound, setNoDataFound] = useState(false);
  const loadMoreRef = useRef(null);

  const footerRef = useRef(null);

  const [isFooterVisible, setIsFooterVisible] = useState(false);

  const locationRef = useRef({
    latitude: "",
    longitude: "",
  });

  const [itemsPerPage, setItemsPerPage] = useState(() =>
    getResponsiveItemsPerPage(),
  );

  const [customizationHistory, setCustomizationHistory] = useState({});

  const [repeatCustomizationItem, setRepeatCustomizationItem] = useState(null);

  const getItemCustomizations = (item) => {
    const firstPrice = Array.isArray(item?.prices) ? item.prices[0] : null;

    return Array.isArray(firstPrice?.customizations)
      ? firstPrice.customizations
      : [];
  };

  const openItemDetails = (item, restaurant, initialPriceIndex = 0) => {
    const totalPrices = Array.isArray(item?.prices) ? item.prices.length : 0;

    const safeIndex =
      initialPriceIndex >= 0 && initialPriceIndex < totalPrices
        ? initialPriceIndex
        : 0;

    setSelectedDetailPriceIndex(safeIndex);

    setSelectedItemDetail({
      ...item,
      restaurant,
    });
  };

  const handleCustomizeItem = (item, restaurant) => {
    setSelectedCustomizationItem({
      ...item,
      restaurant,
    });

    setCustomizationValues({});
  };

  // =========================================================
  // ADD OFFER ITEM TO CART
  // =========================================================

  const handleAddItem = (item, restaurant, selectedCustomizations = {}) => {
    const restaurantUserId = String(
      restaurant?.restaurantId || item?.restaurantId || "",
    ).trim();

    if (!restaurantUserId) {
      console.error("Restaurant userid missing for cart item:", item);

      return;
    }

    if (isOfferRestaurantClosed(restaurant)) {
      console.warn(
        "Restaurant is closed. Item cannot be added:",
        restaurantUserId,
      );

      return;
    }

    const currentOrderType = String(
      selectedOrderType || "home_delivery",
    ).trim();

    // -------------------------------------------------------
    // NORMALIZE OFFER ITEM FOR CART
    // -------------------------------------------------------

    const cartItem = {
      ...item,

      item_id: item?.item_id || item?.itemId || "",

      item_name: item?.item_name || item?.itemName || "Item",

      item_description: item?.item_description || item?.itemDescription || "",

      photo: item?.photo || item?.itemImage || "",

      prices: Array.isArray(item?.prices) ? item.prices : [],
    };

    // -------------------------------------------------------
    // CATEGORY
    // -------------------------------------------------------

    const category = {
      cat_id: item?.catId || item?.categoryId || "",

      category_name: item?.categoryName || "",
    };

    // -------------------------------------------------------
    // RESTAURANT
    // -------------------------------------------------------

    const restaurantData = {
      userId: restaurantUserId,

      userid: restaurantUserId,

      user_id: restaurantUserId,

      name: restaurant?.restaurantName || item?.restaurantName || "Restaurant",

      restaurant_name:
        restaurant?.restaurantName || item?.restaurantName || "Restaurant",

      image: restaurant?.restaurantImage || item?.restaurantImage || "",

      restaurant_image:
        restaurant?.restaurantImage || item?.restaurantImage || "",
    };

    // -------------------------------------------------------
    // ADD TO SHARED CART
    // -------------------------------------------------------

    addToCart({
      item: cartItem,
      category,
      restaurant: restaurantData,
      orderType: currentOrderType,
      selectedCustomizations,
    });
  };

  // =========================================================
  // GET RESTAURANT ID
  // =========================================================

  const getOfferRestaurantId = (item, restaurant) => {
    return String(
      restaurant?.restaurantId ||
        restaurant?.userId ||
        restaurant?.userid ||
        item?.restaurantId ||
        item?.restaurant_id ||
        "",
    ).trim();
  };

  // =========================================================
  // CUSTOMIZABLE ADD / PLUS
  // =========================================================

  const handleOfferCustomizableAdd = (
    item,
    restaurant,
    currentQuantity = 0,
  ) => {
    // =====================================================
    // RESTAURANT CLOSED
    // Modal open ho sakta hai but cart me add nahi hoga
    // =====================================================

    if (isOfferRestaurantClosed(restaurant)) {
      setRepeatCustomizationItem(null);

      handleCustomizeItem(item, restaurant);

      return;
    }

    // =====================================================
    // CART EMPTY / ITEM NOT IN CART
    // FIRST TIME NORMAL CUSTOMIZATION MODAL OPEN
    // =====================================================

    if (Number(itemCount || 0) <= 0 || Number(currentQuantity || 0) <= 0) {
      setRepeatCustomizationItem(null);

      handleCustomizeItem(item, restaurant);

      return;
    }

    // =====================================================
    // CHECK CURRENT SAVED PREFERENCES
    // =====================================================

    const restaurantId = getOfferRestaurantId(item, restaurant);

    const historyKey = buildCustomizationHistoryKey(restaurantId, item);

    const savedPreferences = Array.isArray(customizationHistory?.[historyKey])
      ? customizationHistory[historyKey]
      : [];

    // No previous preference
    if (savedPreferences.length === 0) {
      handleCustomizeItem(item, restaurant);

      return;
    }

    // =====================================================
    // REPEAT PREFERENCE MODAL
    // =====================================================

    setRepeatCustomizationItem({
      item,
      restaurant,
      historyKey,
      preferences: savedPreferences,
    });
  };

  // =========================================================
  // REPEAT
  // =========================================================

  const handleRepeatOfferCustomization = (preference) => {
    if (!repeatCustomizationItem) {
      return;
    }

    handleAddItem(
      repeatCustomizationItem.item,
      repeatCustomizationItem.restaurant,
      preference,
    );

    setRepeatCustomizationItem(null);
  };

  // =========================================================
  // REMOVE ONE ITEM FROM CART
  // =========================================================

  const handleRemoveItem = (item) => {
    const itemId = String(item?.itemId || item?.item_id || "").trim();

    if (!itemId) {
      return;
    }

    decreaseItem(itemId);
  };

  // =========================================================
  // CHECKOUT
  // =========================================================

  const handleCheckout = () => {
    setCartOpen(false);

    navigate("/checkout?startCart=1");
  };

  // =========================================================
  // RESTAURANT NAME CLICK
  // =========================================================
  //
  // IMPORTANT:
  //
  // Item card itself is NOT clickable.
  //
  // Only restaurant name opens:
  //
  // /restaurant-details/:userid
  //
  // =========================================================

  const handleRestaurantClick = async (restaurant) => {
    const restaurantUserId = String(
      restaurant?.restaurantId ||
        restaurant?.userId ||
        restaurant?.userid ||
        restaurant?.user_id ||
        "",
    ).trim();

    if (!restaurantUserId) {
      console.error("Restaurant userid missing:", restaurant);

      return;
    }

    if (checkingRestaurantId === restaurantUserId) {
      return;
    }

    const currentOrderType = String(
      selectedOrderType || "home_delivery",
    ).trim();

    const currentPlatform = String(selectedPlatform || "").trim();

    // =====================================================
    // RESTAURANT DATA
    // =====================================================

 const selectedRestaurantData = {
   userId: restaurantUserId,
   name: restaurant?.restaurantName || restaurant?.name || "Restaurant",
   image: restaurant?.restaurantImage || restaurant?.image || "",
   catalogueStatus: restaurant?.catalogueStatus || "",
 };
    try {
      setCheckingRestaurantId(restaurantUserId);

      // =====================================================
      // RESTAURANT DETAILS API
      // =====================================================

      const detailsResult = await getRestaurantDetails({
        user_id: restaurantUserId,
      });

      console.log("OFFER PAGE RESTAURANT DETAILS RESULT:", detailsResult);

      // API fail hone par restaurant page block nahi hoga
      const apiRestaurantDetails =
        detailsResult?.success && detailsResult?.data
          ? detailsResult.data
          : null;

      if (!detailsResult?.success) {
        console.error(
          detailsResult?.message || "Unable to fetch restaurant details.",
        );
      }

      // =====================================================
      // SAVE SELECTED RESTAURANT
      // =====================================================

      selectRestaurant(selectedRestaurantData);

      // =====================================================
      // OPEN RESTAURANT PAGE
      // =====================================================

      navigate(`/restaurant-details/${restaurantUserId}`, {
        state: {
          restaurant: selectedRestaurantData,

          orderType: currentOrderType,

          platform: currentPlatform,

          selectedItemName: "",

          // Restaurant Details API Response
          restaurantDetails: apiRestaurantDetails,
        },
      });
    } catch (error) {
      console.error("Offer restaurant click error:", error);

      // API/network issue ke case me bhi old flow chalne do
      selectRestaurant(selectedRestaurantData);

      navigate(`/restaurant-details/${restaurantUserId}`, {
        state: {
          restaurant: selectedRestaurantData,

          orderType: currentOrderType,

          platform: currentPlatform,

          selectedItemName: "",

          restaurantDetails: null,
        },
      });
    } finally {
      setCheckingRestaurantId("");
    }
  };
  // =========================================================
  // RESTAURANT NAME KEYBOARD CLICK
  // =========================================================

  const handleRestaurantKeyDown = (event, restaurant) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();

    handleRestaurantClick(restaurant);
  };

  // =========================================================
  // MOVE COMPLETE VISIBLE GROUP + INFINITE LOOP
  // =========================================================

  const updateSliderEdgeState = (restaurantKey) => {
    const slider = restaurantSliderRefs.current[restaurantKey];

    if (!slider) {
      return;
    }

    // =====================================================
    // SCROLL EDGE STATE
    // =====================================================

    const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);

    const currentScroll = slider.scrollLeft;

    const atStart = currentScroll <= 5;

    const atEnd = maxScroll <= 5 || currentScroll >= maxScroll - 5;

    setSliderEdgeState((previous) => {
      const oldState = previous[restaurantKey];

      if (oldState?.atStart === atStart && oldState?.atEnd === atEnd) {
        return previous;
      }

      return {
        ...previous,

        [restaurantKey]: {
          atStart,
          atEnd,
        },
      };
    });

    // =====================================================
    // KEEP SLIDER HEIGHT BASED ONLY ON VISIBLE CARDS
    // =====================================================

    const sliderRect = slider.getBoundingClientRect();

    const cards = Array.from(slider.children);

    const visibleCards = cards.filter((card) => {
      const cardRect = card.getBoundingClientRect();

      return (
        cardRect.right > sliderRect.left + 2 &&
        cardRect.left < sliderRect.right - 2
      );
    });

    if (visibleCards.length === 0) {
      return;
    }

    const visibleCardHeight = Math.max(
      ...visibleCards.map((card) =>
        Math.ceil(card.getBoundingClientRect().height),
      ),
    );

    if (visibleCardHeight > 0) {
      // +8 = slider ke pb-2 ke liye
      slider.style.height = `${visibleCardHeight + 8}px`;
    }
  };

  const moveRestaurantSlider = (restaurantKey, direction, totalItems) => {
    const slider = restaurantSliderRefs.current[restaurantKey];

    if (!slider || totalItems <= itemsPerPage) {
      return;
    }

    const firstCard = slider.firstElementChild;

    if (!firstCard) {
      return;
    }

    const cardWidth = firstCard.getBoundingClientRect().width;

    const sliderStyles = window.getComputedStyle(slider);

    const gap =
      Number.parseFloat(sliderStyles.columnGap || sliderStyles.gap || "0") || 0;

    const cardStep = cardWidth + gap;

    const scrollAmount = cardStep * itemsPerPage;

    const currentScroll = slider.scrollLeft;

    const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);

    // =====================================================
    // FORWARD
    // =====================================================

    if (direction === "next") {
      // Already last position par hai
      if (currentScroll >= maxScroll - 5) {
        return;
      }

      const targetScroll = Math.min(currentScroll + scrollAmount, maxScroll);

      slider.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });

      return;
    }

    // =====================================================
    // BACKWARD
    // =====================================================

    if (direction === "previous") {
      // Already first position par hai
      if (currentScroll <= 5) {
        return;
      }

      const targetScroll = Math.max(currentScroll - scrollAmount, 0);

      slider.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
    }
  };

  const groupedRestaurants = useMemo(() => {
    const grouped = {};

    items.forEach((item) => {
      const restaurantKey =
        item?.restaurantId || item?.restaurantName || "restaurant";

      if (!grouped[restaurantKey]) {
        grouped[restaurantKey] = {
          restaurantId: item?.restaurantId || "",

          restaurantName: item?.restaurantName || "Restaurant",

          restaurantImage: item?.restaurantImage || "",

          catalogueStatus: String(
            item?.catalogueStatus || item?.catalogue_status || "",
          )
            .trim()
            .toLowerCase(),

          items: [],
        };
      }

      grouped[restaurantKey].items.push(item);
    });

    return Object.values(grouped);
  }, [items]);

  // =========================================================
  // RESTAURANT ORDER
  // OPEN FIRST
  // CLOSED ONLY AFTER ALL PAGINATION IS FINISHED
  // =========================================================

  const sortedGroupedRestaurants = useMemo(() => {
    const openRestaurants = [];
    const closedRestaurants = [];

    groupedRestaurants.forEach((restaurant) => {
      if (isOfferRestaurantClosed(restaurant)) {
        closedRestaurants.push(restaurant);
      } else {
        openRestaurants.push(restaurant);
      }
    });

    // =======================================================
    // MORE PAGES AVAILABLE
    //
    // Future pagination me aur OPEN restaurants aa sakte hain.
    // Isliye abhi CLOSED restaurants show nahi honge.
    // =======================================================

    if (hasMore) {
      return openRestaurants;
    }

    // =======================================================
    // PAGINATION COMPLETED
    //
    // Ab saare OPEN first
    // aur saare CLOSED unke baad.
    // =======================================================

    return [...openRestaurants, ...closedRestaurants];
  }, [groupedRestaurants, hasMore]);

  useEffect(() => {
    let animationFrameId = null;

    const checkFooterPosition = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        const footer = footerRef.current;

        if (!footer) {
          setIsFooterVisible(false);
          return;
        }

        const footerRect = footer.getBoundingClientRect();

        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight;

        /*
         * Footer ka TOP viewport ke andar aate hi
         * View Cart hide hoga.
         *
         * Footer screen ke neeche hai to cart visible rahega.
         */
        const footerReached =
          footerRect.top <= viewportHeight && footerRect.bottom > 0;

        setIsFooterVisible((previous) =>
          previous === footerReached ? previous : footerReached,
        );
      });
    };

    checkFooterPosition();

    window.addEventListener("scroll", checkFooterPosition, {
      passive: true,
    });

    window.addEventListener("resize", checkFooterPosition);

    return () => {
      window.removeEventListener("scroll", checkFooterPosition);

      window.removeEventListener("resize", checkFooterPosition);

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [items.length]);

  useEffect(() => {
    const handleResize = () => {
      const nextItemsPerPage = getResponsiveItemsPerPage();
      setItemsPerPage((previous) => {
        if (previous === nextItemsPerPage) {
          return previous;
        }

        return nextItemsPerPage;
      });
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    lastLoadMoreTimeRef.current = 0;
  }, [
    value,
    selectedDeliveryLocation?.latitude,
    selectedDeliveryLocation?.longitude,
  ]);

  // =========================================================
  // FETCH FIRST PAGE
  // =========================================================
  useEffect(() => {
    if (!value) {
      return;
    }

    const loadData = async () => {
      try {
        // Reset no-data state whenever a new search starts
        setNoDataFound(false);

        const latitude = selectedDeliveryLocation?.latitude;
        const longitude = selectedDeliveryLocation?.longitude;

        if (!latitude || !longitude) {
          console.warn("Delivering To location is not available");
          return;
        }

        locationRef.current = {
          latitude,
          longitude,
        };

        await fetchOfferData({
          text: value,
          latitude,
          longitude,
          reset: true,
        });
      } catch (fetchError) {
        console.error("Offer loading error:", fetchError);
      }
    };

    loadData();
  }, [
    value,
    selectedDeliveryLocation?.latitude,
    selectedDeliveryLocation?.longitude,
    fetchOfferData,
  ]);

  // =========================================================
  // SHOW NO DATA AFTER 5 SECONDS
  // =========================================================
  useEffect(() => {
    setNoDataFound(false);

    if (!value) {
      return;
    }

    const timer = setTimeout(() => {
      // Only show no-data when API has finished
      // and there are still no items.
      if (!loading && !loadingMore && items.length === 0) {
        setNoDataFound(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [value, loading, loadingMore, items.length]);

  // =========================================================
  // INFINITE SCROLL / LOAD NEXT PAGE
  // =========================================================

  useEffect(() => {
    const target = loadMoreRef.current;

    if (!target) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry?.isIntersecting) {
          return;
        }

        // API already running
        if (loading || loadingMore) {
          return;
        }

        // Backend says no more pages
        if (!hasMore) {
          return;
        }

        const latitude = locationRef.current.latitude;
        const longitude = locationRef.current.longitude;

        if (!latitude || !longitude || !value) {
          return;
        }

        // =====================================================
        // LOAD MORE COOLDOWN
        // Baar-baar rapidly API fire nahi hogi
        // =====================================================

        const currentTime = Date.now();

        if (currentTime - lastLoadMoreTimeRef.current < LOAD_MORE_COOLDOWN) {
          return;
        }

        lastLoadMoreTimeRef.current = currentTime;

        // =====================================================
        // NEXT PAGE
        // =====================================================

        fetchOfferData({
          text: value,
          latitude,
          longitude,
          reset: false,
        });
      },
      {
        root: null,

        // Bottom ke bahut pehle API call nahi hogi
        rootMargin: "100px 0px",

        threshold: 0.01,
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [value, loading, loadingMore, hasMore, fetchOfferData]);

  // =========================================================
  // ERROR
  // =========================================================

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-sm">
          <p className="font-semibold text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  // =========================================================
  // UI
  // =========================================================

  return (
    <>
      <ShimmerStyles />

      <Navbar />
      {/* MAIN PAGE */}

      <div className="min-h-screen bg-gray-100">
        <div className="mx-auto w-full max-w-7xl px-3 py-4 min-[480px]:px-4 sm:px-6 sm:py-6 lg:px-8">
          {/* ================================================= */}
          {/* SELECTED OFFER BANNER */}
          {/* ================================================= */}
          {selectedBanner && bannerFallbackUrl && (
            <div className="mb-5 w-full sm:mb-6">
              <div
                className="
        w-full
        overflow-hidden
        rounded-[14px]
        bg-gray-100
        shadow-sm

        aspect-[16/7]

        sm:rounded-[18px]

        md:aspect-[16/5]

        lg:h-[220px]
        lg:aspect-auto

        xl:h-[280px]
      "
              >
                <img
                  src={bannerMobileUrl || bannerFallbackUrl}
                  alt={
                    selectedBanner?.banner_name ||
                    selectedBanner?.banner_title ||
                    selectedBanner?.banner_subtitle ||
                    "Offer banner"
                  }
                  className="
          block
          h-full
          w-full
          object-fill
        "
                  loading="eager"
                />
              </div>
            </div>
          )}

          {/* {selectedBanner && bannerFallbackUrl && (
            <div className="mb-5 w-full sm:mb-6">
              <div
                className="
        w-full
        overflow-hidden
        rounded-[14px]
        bg-gray-100
        shadow-sm

        aspect-[16/7]

        sm:rounded-[18px]

        md:aspect-[16/5]

        lg:h-[220px]
        lg:aspect-auto

        xl:h-[280px]
      "
              >
                <picture className="block h-full w-full">
               
                  {bannerDesktopUrl && (
                    <source
                      media="(min-width: 1024px)"
                      srcSet={bannerDesktopUrl}
                    />
                  )}

                  {bannerTabletUrl && (
                    <source
                      media="(min-width: 768px)"
                      srcSet={bannerTabletUrl}
                    />
                  )}

                  <img
                    src={
                      bannerMobileUrl ||
                      bannerTabletUrl ||
                      bannerDesktopUrl ||
                      bannerFallbackUrl
                    }
                    alt={
                      selectedBanner?.banner_name ||
                      selectedBanner?.banner_title ||
                      selectedBanner?.banner_subtitle ||
                      "Offer banner"
                    }
                    className="
            block
            h-full
            w-full
            object-cover
            object-center
          "
                    loading="eager"
                  />
                </picture>
              </div>
            </div>
          )} */}

          {/* ================================================= */}
          {/* PAGE HEADER */}
          {/* ================================================= */}

          <div className="mb-6 sm:mb-8">
            <h1 className="break-words text-[22px] font-bold leading-tight text-gray-900 min-[480px]:text-2xl sm:text-3xl">
              {title}
            </h1>

            <p className="mt-1 text-xs text-gray-500 min-[480px]:text-sm">
              Explore dishes restaurant-wise
            </p>

            {items.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400 sm:text-xs">
                <span>{items.length} items</span>

                <span>•</span>

                <span>{groupedRestaurants.length} restaurants</span>
              </div>
            )}
          </div>

          {/* ================================================= */}
          {/* EMPTY */}
          {/* ================================================= */}

          {groupedRestaurants.length === 0 ? (
            <>
              {noDataFound ? (
                <div className="flex min-h-[300px] items-center justify-center">
                  <div className="flex flex-col items-center gap-2 text-center">
                    <p className="text-base font-semibold text-gray-700">
                      No items found
                    </p>

                    <p className="text-sm text-gray-500">
                      We couldn't find any items matching "{value}".
                    </p>
                  </div>
                </div>
              ) : (
                <OfferItemsSkeleton />
              )}
            </>
          ) : (
            // =================================================
            // RESTAURANT SECTIONS
            // =================================================

            <div className="space-y-3 sm:space-y-4">
              {sortedGroupedRestaurants.map((restaurant) => {
                // ===========================================
                // RESTAURANT KEY
                // ===========================================

                const restaurantKey =
                  restaurant.restaurantId || restaurant.restaurantName;

                const restaurantUserId = String(
                  restaurant?.restaurantId || "",
                ).trim();

                const restaurantCatalogueStatus = String(
                  restaurant?.catalogueStatus || "",
                )
                  .trim()
                  .toLowerCase();
                const closedByCatalogueStatus =
                  restaurantCatalogueStatus !== "show";

                const isRestaurantClosed = closedByCatalogueStatus;

                // ===========================================
                // CURRENT START INDEX
                // ===========================================

                const totalRestaurantItems = restaurant.items.length;

                const showNavigation = totalRestaurantItems > itemsPerPage;

                const currentSliderState = sliderEdgeState[restaurantKey] || {
                  atStart: true,
                  atEnd: false,
                };

                const isAtStart = currentSliderState.atStart;

                const isAtEnd = currentSliderState.atEnd;

                const loopedItems = restaurant.items;
                return (
                  <section key={restaurantKey}>
                    {/* ===================================== */}
                    {/* RESTAURANT HEADER */}
                    {/* ===================================== */}

                    <div className="mb-3 flex min-w-0 items-center justify-between gap-2 sm:mb-4 sm:gap-4">
                      <div className="flex min-w-0 flex-1 items-center gap-2.5 sm:gap-3">
                        <img
                          src={restaurant?.restaurantImage || ChaafoLogo}
                          alt={restaurant?.restaurantName || "Restaurant"}
                          className="h-10 w-10 shrink-0 rounded-full border border-gray-200 bg-white object-contain p-1 shadow-sm min-[480px]:h-11 min-[480px]:w-11 sm:h-12 sm:w-12 sm:p-0.5"
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = ChaafoLogo;
                            event.currentTarget.className =
                              "h-10 w-10 shrink-0 rounded-full border border-gray-200 bg-white object-contain p-1 shadow-sm min-[480px]:h-11 min-[480px]:w-11 sm:h-12 sm:w-12 sm:p-0.5";
                          }}
                        />

                        <div className="min-w-0 flex-1">
                          <button
                            type="button"
                            onClick={() => handleRestaurantClick(restaurant)}
                            onKeyDown={(event) =>
                              handleRestaurantKeyDown(event, restaurant)
                            }
                            className="block max-w-full cursor-pointer truncate text-left text-[15px] font-bold text-gray-900 transition hover:text-[#2B4578] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2B4578]/30 min-[480px]:text-base sm:text-xl"
                          >
                            {restaurant.restaurantName}
                          </button>

                          <p className="mt-0.5 truncate text-[10px] text-gray-500 min-[480px]:text-[11px] sm:text-xs">
                            {restaurant.items.length}{" "}
                            {restaurant.items.length === 1 ? "item" : "items"}{" "}
                            available
                          </p>
                        </div>
                      </div>

                      {showNavigation && (
                        <div className="hidden shrink-0 items-center gap-2 sm:flex">
                          <button
                            type="button"
                            disabled={isAtStart}
                            onClick={() =>
                              moveRestaurantSlider(
                                restaurantKey,
                                "previous",
                                restaurant.items.length,
                              )
                            }
                            aria-label="Previous items"
                            className={`
    flex
    h-9
    w-9
    items-center
    justify-center
    rounded-full
    border
    shadow-sm
    transition
    lg:h-10
    lg:w-10
    ${
      isAtStart
        ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-300 opacity-60"
        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-100 active:scale-95"
    }
  `}
                          >
                            <ArrowLeft size={19} />
                          </button>

                          <button
                            type="button"
                            disabled={isAtEnd}
                            onClick={() =>
                              moveRestaurantSlider(
                                restaurantKey,
                                "next",
                                restaurant.items.length,
                              )
                            }
                            aria-label="Next items"
                            className={`
    flex
    h-9
    w-9
    items-center
    justify-center
    rounded-full
    border
    shadow-sm
    transition
    lg:h-10
    lg:w-10
    ${
      isAtEnd
        ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-300 opacity-60"
        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-100 active:scale-95"
    }
  `}
                          >
                            <ArrowRight size={19} />
                          </button>
                        </div>
                      )}
                    </div>
                    {/* ===================================== */}
                    {/* DIVIDER */}
                    {/* ===================================== */}

                    {/* <div className="mb-4 h-px bg-gray-200" /> */}

                    {/* ===================================== */}
                    {/* ITEMS */}
                    {/* ===================================== */}

                    <div
                      ref={(element) => {
                        if (element) {
                          restaurantSliderRefs.current[restaurantKey] = element;

                          requestAnimationFrame(() => {
                            updateSliderEdgeState(restaurantKey);
                          });
                        } else {
                          delete restaurantSliderRefs.current[restaurantKey];
                        }
                      }}
                      onScroll={() => updateSliderEdgeState(restaurantKey)}
                      className="
  flex
  snap-x
  snap-mandatory
  gap-4

  overflow-x-auto
  overflow-y-hidden

  overscroll-x-contain
  scroll-smooth

  pb-2

  [scrollbar-width:none]
  [-ms-overflow-style:none]
  [&::-webkit-scrollbar]:hidden
"
                    >
                      {loopedItems.map((item, loopIndex) => {
                        const itemId = String(
                          item?.itemId || item?.item_id || "",
                        );

                        const quantity = getQuantity(itemId);

                        const priceOptions = getOfferPriceOptions(item);

                        const priceKey = `${restaurant.restaurantId}-${itemId}`;

                        const selectedPriceIndex =
                          selectedPriceIndexes[priceKey] ?? 0;

                        const selectedPrice =
                          priceOptions[selectedPriceIndex] ||
                          priceOptions[0] ||
                          null;

                        // Selected offer variant
                        const selectedVariantItem = buildOfferVariantItem(
                          item,
                          selectedPrice,
                        );

                        // Selected size ki customization
                        const hasCustomizations =
                          Array.isArray(selectedPrice?.customizations) &&
                          selectedPrice.customizations.length > 0;

                        const visiblePriceOptions = priceOptions.slice(0, 2);

                        const remainingPriceOptions = Math.max(
                          priceOptions.length - 2,
                          0,
                        );

                        return (
                          <article
                            key={`${item.restaurantId}-${item.itemId}-${loopIndex}`}
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              openItemDetails(
                                item,
                                restaurant,
                                selectedPriceIndex,
                              )
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();

                                openItemDetails(
                                  item,
                                  restaurant,
                                  selectedPriceIndex,
                                );
                              }
                            }}
                            className="
  group
  snap-start
  self-start
  shrink-0
  cursor-pointer
  overflow-hidden
  rounded-xl
  border
  border-gray-100
  bg-white
  shadow-sm
  transition
  duration-200
  hover:-translate-y-0.5
  hover:shadow-md
  focus:outline-none
  sm:rounded-2xl
"
                            style={{
                              width: `calc((100% - ${
                                (itemsPerPage - 1) * 16
                              }px) / ${itemsPerPage})`,
                            }}
                          >
                            {/* ========================= */}
                            {/* IMAGE */}
                            {/* ========================= */}

                            <div className="relative flex h-[100px] w-full items-center justify-center bg-white min-[480px]:h-[120px] sm:h-[130px] md:h-[140px] lg:h-[150px]">
                              <ItemImageWithShimmer
                                src={item?.itemImage}
                                alt={item?.itemName || "Item"}
                                fallback={NoImg}
                                className=""
                              />
                              {/* DISH ICON - TOP LEFT */}

                              {item.dishIcon && (
                                <div className="absolute left-2 top-2 z-10 flex h-5 w-5 items-center justify-center bg-white/95 shadow-sm">
                                  <img
                                    src={item.dishIcon}
                                    alt={item.dishType || "Dish type"}
                                    className="h-4 w-4 object-contain"
                                  />
                                </div>
                              )}

                              {/* RATING - TOP RIGHT */}

                              {Number(item.ratings) > 0 && (
                                <div className="absolute right-2 top-2 z-20 flex items-center gap-1 rounded-full bg-[#16a34a] px-2 py-1 text-[10px] font-semibold text-white shadow-md sm:right-3 sm:top-3 sm:px-2.5 sm:text-[12px]">
                                  <Star
                                    size={12}
                                    strokeWidth={0}
                                    fill="currentColor"
                                  />

                                  <span>{Number(item.ratings).toFixed(1)}</span>
                                </div>
                              )}

                              {/* OUT OF STOCK */}

                              {!item.inStock && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/35">
                                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-800">
                                    Out of Stock
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* ========================= */}
                            {/* CONTENT */}
                            {/* ========================= */}

                            <div className="p-2 sm:p-4">
                              {/* ITEM NAME */}

                              <h3
                                title={item?.itemName || ""}
                                className="w-full truncate whitespace-nowrap text-sm font-semibold leading-5 text-gray-900 sm:text-base"
                              >
                                {item.itemName}
                              </h3>

                              {/* =============================================== */}
                              {/* SIZE / PRICE OPTIONS */}
                              {/* =============================================== */}

                              {priceOptions.length > 0 && (
                                <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1 sm:gap-1.5">
                                  {visiblePriceOptions.map(
                                    (priceOption, index) => {
                                      const isSelected =
                                        selectedPriceIndex === index;

                                      return (
                                        <button
                                          key={`${itemId}-${priceOption.sizeId}-${index}`}
                                          type="button"
                                          onClick={(event) => {
                                            event.stopPropagation();

                                            setSelectedPriceIndexes(
                                              (previous) => ({
                                                ...previous,
                                                [priceKey]: index,
                                              }),
                                            );
                                          }}
                                          className={`
            inline-flex
            items-center
            justify-center
            gap-1

            rounded-full
            border

            px-2
            py-1

            text-[8px]
            font-semibold
            leading-none

            transition

            min-[480px]:text-[9px]

            sm:px-2.5
            sm:text-[10px]

            ${
              isSelected
                ? `
                    border-[#2454e6]
                    bg-[#eef4ff]
                    text-[#2454e6]
                  `
                : `
                    border-gray-200
                    bg-gray-50
                    text-gray-600

                    hover:border-[#2454e6]
                  `
            }
          `}
                                        >
                                          <span>{priceOption.size}</span>
                                        </button>
                                      );
                                    },
                                  )}

                                  {/* MORE */}

                                  {remainingPriceOptions > 0 && (
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();

                                        openItemDetails(
                                          item,
                                          restaurant,
                                          selectedPriceIndex,
                                        );
                                      }}
                                      className="
          inline-flex
          items-center
          justify-center

          rounded-full

          border
          border-[#dce2e8]

          bg-[#f8fafc]

          px-2
          py-1

          text-[8px]
          font-semibold
          leading-none
          text-[#2454e6]

          transition

          hover:border-[#2454e6]
          hover:bg-[#eef4ff]

          min-[480px]:text-[9px]

          sm:px-2.5
          sm:text-[10px]
        "
                                    >
                                      +{remainingPriceOptions} More
                                    </button>
                                  )}

                                  {/* CUSTOMIZABLE */}

                                  {hasCustomizations && (
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();

                                        handleOfferCustomizableAdd(
                                          selectedVariantItem,
                                          restaurant,
                                          quantity,
                                        );
                                      }}
                                      className="
          inline-flex
          rounded-full
          border
          border-blue-200
          bg-blue-50

          px-2
          py-1

          text-[8px]
          font-semibold
          text-blue-700
        "
                                    >
                                      Customizable
                                    </button>
                                  )}
                                </div>
                              )}

                              {/* DESCRIPTION */}

                              {/* {item.itemDescription && (
                                <p className="mt-1 line-clamp-2 text-xs leading-4 text-gray-500">
                                  {item.itemDescription}
                                </p>
                              )} */}

                              {/* ======================= */}
                              {/* PRICE + ADD */}
                              {/* ======================= */}

                              <div className="mt-2 flex min-w-0 items-end justify-between gap-2">
                                {/* PRICE */}

                                <div className="min-w-0 flex-1">
                                  {selectedPrice ? (
                                    <div className="flex min-w-0 items-center gap-1.5">
                                      <span className="text-[15px] font-bold text-gray-900 sm:text-lg">
                                        {selectedPrice.formattedSellingPrice}
                                      </span>

                                      {selectedPrice.hasDiscount && (
                                        <span className="text-[10px] text-gray-400 line-through sm:text-xs">
                                          {selectedPrice.formattedOriginalPrice}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400">
                                      Price unavailable
                                    </span>
                                  )}
                                </div>

                                {/* ADD / QUANTITY */}

                                {item.inStock && (
                                  <>
                                    {quantity === 0 ? (
                                      <button
                                        type="button"
                                        disabled={isRestaurantClosed}
                                        onClick={(event) => {
                                          event.stopPropagation();

                                          if (
                                            !selectedPrice ||
                                            isRestaurantClosed
                                          ) {
                                            return;
                                          }

                                          if (hasCustomizations) {
                                            handleOfferCustomizableAdd(
                                              selectedVariantItem,
                                              restaurant,
                                              quantity,
                                            );
                                          } else {
                                            handleAddItem(
                                              selectedVariantItem,
                                              restaurant,
                                            );
                                          }
                                        }}
                                        className="
            flex
            h-8
            min-w-[60px]
            shrink-0
            items-center
            justify-center
            rounded-full
           bg-[#1d4ed8]
px-3
text-[11px]
font-bold
text-white
transition

hover:bg-[#1a47c2]
active:scale-95

disabled:cursor-not-allowed
disabled:bg-gray-300
disabled:text-gray-500
disabled:hover:bg-gray-300
disabled:active:scale-100
disabled:shadow-none

            min-[480px]:min-w-[65px]

            sm:h-9
            sm:min-w-[70px]
            sm:px-4
            sm:text-[13px]
          "
                                      >
                                        ADD
                                      </button>
                                    ) : (
                                      <div
                                        className="
            flex
            h-8
            shrink-0
            items-center
            overflow-hidden
            rounded-full
            bg-[#1d4ed8]
            text-white

            sm:h-9
          "
                                        onClick={(event) =>
                                          event.stopPropagation()
                                        }
                                      >
                                        {/* MINUS */}

                                        <button
                                          type="button"
                                          onClick={(event) => {
                                            event.stopPropagation();

                                            handleRemoveItem(item);
                                          }}
                                          className="flex h-full w-8 items-center justify-center text-base font-semibold sm:w-9 sm:text-lg"
                                        >
                                          −
                                        </button>

                                        {/* QUANTITY */}

                                        <span className="min-w-5 text-center text-[11px] font-semibold sm:min-w-7 sm:text-[13px]">
                                          {quantity}
                                        </span>

                                        {/* PLUS */}

                                        <button
                                          type="button"
                                          disabled={isRestaurantClosed}
                                          onClick={(event) => {
                                            event.stopPropagation();

                                            if (
                                              !selectedPrice ||
                                              isRestaurantClosed
                                            ) {
                                              return;
                                            }

                                            if (hasCustomizations) {
                                              handleOfferCustomizableAdd(
                                                selectedVariantItem,
                                                restaurant,
                                                quantity,
                                              );
                                            } else {
                                              handleAddItem(
                                                selectedVariantItem,
                                                restaurant,
                                              );
                                            }
                                          }}
                                          className="
    flex
    h-full
    w-8
    items-center
    justify-center
    text-base
    font-semibold

    disabled:cursor-not-allowed
    disabled:opacity-40

    sm:w-9
    sm:text-lg
  "
                                        >
                                          +
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>

                    {/* ===================================== */}
                    {/* CURRENT POSITION */}
                    {/* ===================================== */}

                    {showNavigation && (
                      <div className="mt-3 text-right text-xs text-gray-400">
                        Showing {itemsPerPage} of {restaurant.items.length}{" "}
                        items
                      </div>
                    )}
                  </section>
                );
              })}

              {/* LOAD MORE / INFINITE SCROLL TRIGGER */}

              <div
                ref={loadMoreRef}
                className={`
    flex
    w-full
    items-center
    justify-center

    ${loadingMore ? "min-h-[150px] py-5" : "min-h-0 py-2"}
  `}
              >
                {loadingMore && (
                  <>
                    {/* MOBILE + TABLET */}
                    <div className="flex items-center justify-center py-4 lg:hidden">
                      <div
                        className="
    h-7
    w-7
    animate-spin
    rounded-full
    border-[3px]
    border-gray-200
    border-t-blue-600
  "
                      />
                    </div>

                    {/* DESKTOP */}
                    <div className="hidden lg:block">
                      <LoadingScreen text="Loading more items..." />
                    </div>
                  </>
                )}

                {!loadingMore && !hasMore && items.length > 0 && (
                  <p className="text-xs text-gray-400">No more items</p>
                )}
              </div>
              <div
                id="offer-items-end-marker"
                className="h-px w-full"
                aria-hidden="true"
              />
            </div>
          )}
        </div>
      </div>

      {/* =================================================== */}
      {/* FLOATING VIEW CART BUTTON */}
      {/* =================================================== */}

      {itemCount > 0 && !isFooterVisible && (
        <button
          id="offer-view-cart-button"
          type="button"
          onClick={() => setCartOpen(true)}
          className="
      fixed
      z-[150]

      bottom-[max(14px,env(safe-area-inset-bottom))]

      left-[72%]
      -translate-x-1/2

      flex
      h-[56px]
      w-fit
      max-w-[calc(100vw-20px)]

      items-center

      rounded-full
      bg-blue-700

      pl-2.5
      pr-4

      text-white

      shadow-[0_16px_35px_rgba(29,78,216,0.35)]

      transition-all
      duration-300

      hover:bg-blue-800
      active:scale-[0.99]

      min-[380px]:h-[58px]
      min-[380px]:pl-3
      min-[380px]:pr-4

      sm:left-[55%]
      sm:h-[62px]
      sm:pl-3
      sm:pr-5

      md:left-auto
      md:right-20
      md:translate-x-0

      lg:right-20
    "
        >
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            {/* ============================================= */}
            {/* CART ITEM IMAGES */}
            {/* ============================================= */}

            <div className="flex shrink-0 items-center">
              {/* ONLY FIRST 2 ITEM IMAGES */}
              {cartItems.slice(0, 2).map((cartItem, index) => (
                <div
                  key={`${cartItem.itemId || cartItem.item_id || index}-${index}`}
                  className={`
        relative
        h-9
        w-9
        shrink-0
        overflow-hidden
        rounded-full
        border-2
        border-blue-700
        bg-white
        shadow-md

        min-[400px]:h-10
        min-[400px]:w-10

        sm:h-11
        sm:w-11

        ${index > 0 ? "-ml-4 sm:-ml-5" : ""}
      `}
                  style={{
                    zIndex: 10 - index,
                  }}
                >
                  <img
                    src={
                      cartItem.image ||
                      cartItem.itemImage ||
                      cartItem.photo ||
                      NoImg
                    }
                    alt={
                      cartItem.name ||
                      cartItem.itemName ||
                      cartItem.item_name ||
                      "Cart item"
                    }
                    className="h-full w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = NoImg;
                    }}
                  />
                </div>
              ))}

              {/* AFTER 2 ITEMS SHOW + COUNT */}
              {cartItems.length > 2 && (
                <div
                  className="
        relative
        z-[5]

        -ml-3
        sm:-ml-4

        flex
        h-9
        w-9
        shrink-0
        items-center
        justify-center

        rounded-full
        border-2
        border-blue-700
        bg-white

        text-[11px]
        font-bold
        text-blue-700

        shadow-md

        min-[400px]:h-10
        min-[400px]:w-10

        sm:h-11
        sm:w-11
        sm:text-xs
      "
                >
                  +{cartItems.length - 2}
                </div>
              )}
            </div>

            {/* ============================================= */}
            {/* CART TEXT */}
            {/* ============================================= */}

            <span className="shrink-0 text-left">
              <span className="block whitespace-nowrap text-[14px] font-bold sm:text-[15px]">
                View Cart
              </span>

              <span className="block whitespace-nowrap text-[11px] text-blue-100 sm:text-xs">
                {itemCount} {itemCount === 1 ? "item" : "items"}
              </span>
            </span>
          </div>
        </button>
      )}

      {/* =================================================== */}
      {/* CART DRAWER */}
      {/* =================================================== */}

      {selectedItemDetail &&
        (() => {
          const detailPriceOptions = getOfferPriceOptions(selectedItemDetail);

          const selectedDetailPrice =
            detailPriceOptions[selectedDetailPriceIndex] ||
            detailPriceOptions[0] ||
            null;

          const selectedDetailVariant = buildOfferVariantItem(
            selectedItemDetail,
            selectedDetailPrice,
          );

          const hasCustomizations =
            Array.isArray(selectedDetailPrice?.customizations) &&
            selectedDetailPrice.customizations.length > 0;

          const detailItemId = String(
            selectedItemDetail?.itemId || selectedItemDetail?.item_id || "",
          ).trim();

          const detailQuantity = getQuantity(detailItemId);

          const detailRestaurant = selectedItemDetail?.restaurant;

          const isDetailRestaurantClosed =
            isOfferRestaurantClosed(detailRestaurant);

          return (
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-3 py-4 sm:px-4 sm:py-6"
              onClick={() => setSelectedItemDetail(null)}
            >
              <div
                className="flex max-h-[92dvh] w-full max-w-[500px] flex-col overflow-hidden rounded-[20px] bg-white shadow-2xl"
                onClick={(event) => event.stopPropagation()}
              >
                {/* IMAGE */}
                <div className="relative flex h-[280px] w-full shrink-0 items-center justify-center overflow-hidden bg-gray-100 sm:h-[340px] md:h-[380px]">
                  <ItemImageWithShimmer
                    src={
                      selectedItemDetail?.itemImage ||
                      selectedItemDetail?.photo ||
                      ""
                    }
                    alt={selectedItemDetail?.itemName || "Item"}
                    fallback={NoImg}
                    className=""
                  />

                  <button
                    type="button"
                    onClick={() => setSelectedItemDetail(null)}
                    className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-gray-700 shadow-md"
                  >
                    <X size={19} />
                  </button>

                  {!selectedItemDetail?.inStock && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/35">
                      <span className="rounded-full bg-white px-4 py-2 text-sm font-semibold">
                        Out of Stock
                      </span>
                    </div>
                  )}
                </div>

                {/* CONTENT */}
                <div className="min-h-0 flex-1 overflow-y-auto p-4 pb-[max(18px,env(safe-area-inset-bottom))] sm:p-5">
                  {/* TITLE - ONE ROW */}
                  <h2
                    title={selectedItemDetail?.itemName || ""}
                    className="line-clamp-2 text-[18px] font-bold leading-6 text-gray-900 sm:text-[20px]"
                  >
                    {selectedItemDetail?.itemName || "Item"}
                  </h2>

                  {selectedItemDetail?.itemDescription && (
                    <p className="mt-2 text-[12px] leading-4 text-[#707a8a] sm:text-[13px] sm:leading-6 font-normal">
                      {selectedItemDetail?.itemDescription ||
                        selectedItemDetail?.item_description ||
                        "Freshly prepared."}
                    </p>
                  )}

                  {/* =============================================== */}
                  {/* ALL SIZE OPTIONS */}
                  {/* =============================================== */}

                  {detailPriceOptions.length > 0 && (
                    <div className="mt-4">
                      <p className="mb-2 text-[11px] font-semibold text-gray-500 sm:text-[12px]">
                        Select option
                      </p>

                      <div className="flex flex-wrap items-center gap-2">
                        {detailPriceOptions.map((priceOption, index) => {
                          const isSelected = selectedDetailPriceIndex === index;

                          return (
                            <button
                              key={`detail-${priceOption.sizeId}-${index}`}
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();

                                setSelectedDetailPriceIndex(index);
                              }}
                              className={`
              inline-flex
              shrink-0
              items-center
              justify-center

              rounded-full
              border

              px-3
              py-1.5

              text-[10px]
              font-semibold

              whitespace-nowrap

              transition

              sm:text-[11px]

              ${
                isSelected
                  ? `
                      border-[#2454e6]
                      bg-[#2454e6]
                      text-white
                    `
                  : `
                      border-gray-200
                      bg-white
                      text-gray-600

                      hover:border-[#2454e6]
                      hover:text-[#2454e6]
                    `
              }
            `}
                            >
                              {priceOption.size}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* =============================================== */}
                  {/* CUSTOMIZABLE */}
                  {/* =============================================== */}

                  {hasCustomizations && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();

                          const currentRestaurant =
                            selectedItemDetail?.restaurant;

                          setSelectedItemDetail(null);

                          setSelectedDetailPriceIndex(0);

                          handleOfferCustomizableAdd(
                            selectedDetailVariant,
                            currentRestaurant,
                            detailQuantity,
                          );
                        }}
                        className="
        inline-flex
        items-center
        justify-center

        rounded-full
        border
        border-blue-200

        bg-blue-50

        px-2.5
        py-1

        text-[10px]
        font-semibold
        text-blue-700

        transition

        hover:bg-blue-100

        sm:text-[11px]
      "
                      >
                        Customizable
                      </button>
                    </div>
                  )}

                  {/* =============================================== */}
                  {/* SELECTED PRICE + ADD */}
                  {/* =============================================== */}

                  <div className="mt-5 flex items-center justify-between gap-4 border-t border-gray-100 pt-4">
                    {/* PRICE */}

                    <div>
                      {selectedDetailPrice ? (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-[20px] font-bold text-gray-900">
                              {selectedDetailPrice.formattedSellingPrice}
                            </span>

                            {selectedDetailPrice.hasDiscount && (
                              <span className="text-[13px] text-gray-400 line-through">
                                {selectedDetailPrice.formattedOriginalPrice}
                              </span>
                            )}
                          </div>

                          <p className="mt-1 text-[11px] text-gray-400">
                            {selectedDetailPrice.size}
                          </p>
                        </>
                      ) : (
                        <span className="text-sm text-gray-400">
                          Price unavailable
                        </span>
                      )}
                    </div>

                    {/* ADD */}

                    {selectedItemDetail?.inStock && (
                      <button
                        type="button"
                        disabled={isDetailRestaurantClosed}
                        onClick={(event) => {
                          event.stopPropagation();

                          if (
                            !selectedDetailPrice ||
                            isDetailRestaurantClosed
                          ) {
                            return;
                          }

                          const currentRestaurant =
                            selectedItemDetail?.restaurant;

                          setSelectedItemDetail(null);

                          setSelectedDetailPriceIndex(0);

                          if (hasCustomizations) {
                            handleOfferCustomizableAdd(
                              selectedDetailVariant,
                              currentRestaurant,
                              detailQuantity,
                            );
                          } else {
                            handleAddItem(
                              selectedDetailVariant,
                              currentRestaurant,
                            );
                          }
                        }}
                        className="
  flex
  h-10
  min-w-[90px]
  items-center
  justify-center

  rounded-full

  bg-[#1d4ed8]

  px-5

  text-[14px]
  font-bold
  text-white

  transition

  hover:bg-[#1a47c2]

  disabled:cursor-not-allowed
  disabled:bg-gray-300
  disabled:text-gray-500
  disabled:hover:bg-gray-300
  disabled:active:scale-100
  disabled:shadow-none
"
                      >
                        ADD
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {selectedCustomizationItem && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 px-3 py-4 sm:px-4 sm:py-6"
          onClick={() => {
            setSelectedCustomizationItem(null);
            setCustomizationValues({});
          }}
        >
          <div
            className="flex max-h-[90dvh] w-full flex-col overflow-hidden rounded-[24px] bg-white shadow-2xl sm:max-w-[500px] sm:rounded-[20px]"
            onClick={(event) => event.stopPropagation()}
          >
            {/* HEADER */}
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-4 sm:items-center sm:px-5">
              <div className="min-w-0 flex-1">
                <h2
                  title={selectedCustomizationItem?.itemName || ""}
                  className="line-clamp-2 text-[16px] font-bold leading-5 text-gray-900 sm:text-[18px] sm:leading-6"
                >
                  Customize {selectedCustomizationItem?.itemName}
                </h2>

                <p className="mt-1 text-[12px] text-gray-500">
                  Select your preferences
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setSelectedCustomizationItem(null);

                  setCustomizationValues({});
                }}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            {/* FIELDS */}
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
              {getItemCustomizations(selectedCustomizationItem).map(
                (customization, index) => {
                  const fieldName =
                    customization?.customize_field || `Option ${index + 1}`;

                  const type = customization?.customization_type;

                  const options = Array.isArray(customization?.options)
                    ? customization.options
                    : [];

                  return (
                    <div key={`${fieldName}-${index}`}>
                      <label className="mb-2 block text-[13px] font-semibold text-gray-800">
                        {fieldName}
                      </label>

                      {type === "input" && (
                        <input
                          type="text"
                          value={customizationValues[fieldName] || ""}
                          onChange={(event) =>
                            setCustomizationValues((previous) => ({
                              ...previous,

                              [fieldName]: event.target.value,
                            }))
                          }
                          className="h-11 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-blue-500"
                        />
                      )}

                      {(type === "select" || type === "customize") &&
                        options.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {options.map((option) => {
                              const isSelected =
                                customizationValues[fieldName] === option;

                              return (
                                <button
                                  key={option}
                                  type="button"
                                  onClick={() =>
                                    setCustomizationValues((previous) => ({
                                      ...previous,

                                      [fieldName]: option,
                                    }))
                                  }
                                  className={`rounded-full border px-3 py-2 text-[12px] font-medium ${
                                    isSelected
                                      ? "border-blue-600 bg-blue-600 text-white"
                                      : "border-gray-200 bg-white text-gray-700"
                                  }`}
                                >
                                  {option}
                                </button>
                              );
                            })}
                          </div>
                        )}
                    </div>
                  );
                },
              )}
            </div>

            {/* DONE */}
            <div className="shrink-0 border-t border-gray-200 px-4 py-4 pb-[max(16px,env(safe-area-inset-bottom))] sm:px-5 sm:pb-4">
              <button
                type="button"
                disabled={isOfferRestaurantClosed(
                  selectedCustomizationItem?.restaurant,
                )}
                onClick={() => {
                  if (
                    isOfferRestaurantClosed(
                      selectedCustomizationItem?.restaurant,
                    )
                  ) {
                    return;
                  }

                  handleAddItem(
                    selectedCustomizationItem,
                    selectedCustomizationItem?.restaurant,
                    customizationValues,
                  );

                  setSelectedCustomizationItem(null);

                  setCustomizationValues({});
                }}
                className="
  h-11
  w-full
  rounded-xl
  bg-blue-700
  text-sm
  font-semibold
  text-white
  transition
  hover:bg-blue-800

  disabled:cursor-not-allowed
  disabled:bg-gray-300
  disabled:text-gray-500
  disabled:hover:bg-gray-300
"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <RepeatCustomizationModal
        open={Boolean(repeatCustomizationItem)}
        itemName={
          repeatCustomizationItem?.item?.itemName ||
          repeatCustomizationItem?.item?.item_name ||
          ""
        }
        preferences={repeatCustomizationItem?.preferences || []}
        onClose={() => setRepeatCustomizationItem(null)}
        onChooseNew={() => {
          const current = repeatCustomizationItem;

          setRepeatCustomizationItem(null);

          if (!current) {
            return;
          }

          handleCustomizeItem(current.item, current.restaurant);
        }}
        onRepeat={handleRepeatOfferCustomization}
      />

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        onCheckout={handleCheckout}
      />
      <div ref={footerRef}>
        <Footer />
      </div>
    </>
  );
};;;;;

export default OfferItems;
